import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { ChevronDown, Filter, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { useSearchParams } from 'react-router';
import { TVideoStatus, VideoCard } from '../model/home';
import { Subject } from '../model/video';
import { mySubjectColors } from '../consts/subject';
import { getMyVideoList } from '../api/myclips';
import { deleteMyVideo } from '../api/home';
import './homepage.css';
import ShareDialog from './ShareDialog';
import { MyVideoCard } from './homepage/MyVideoCard';

interface Video {
  id: string;
  question: string;
  status: 'generating' | 'complete';
  thumbnail: string;
  createdAt: Date;
  subject:
    | 'Mathematics'
    | 'Physics'
    | 'Biology'
    | 'Chemistry'
    | 'History'
    | 'Geography'
    | 'Psychology'
    | 'Computer Science'
    | 'AI'
    | 'Economics'
    | 'Literature'
    | 'Engineering';
  progress: number;
  videoCount: number;
  isFavorite?: boolean;
  duration?: number;
  watchProgress?: number;
}

interface MyVideosPageProps {
  userid?: string | null;
  updateFlag: boolean;
  videos: Video[];
  onVideoClick: (videoId: string) => void;
  onUpdateVideoStatus: (videoId: string, status: TVideoStatus) => void;
  onUpdateVideoProgress: (videoId: string, progress: number) => void;
  onDeleteVideo: (videoId: string) => void;
  onToggleFavorite: (videoId: string) => void;
  onReGenerate: (vid: string) => any;
  handleHasUpdated: () => void;
  onNavigateToHome?: () => void;
  onCancelGenerate: (vid: string) => void;
}

const progressStages = [
  'Analyzing your question...',
  'Generating video structure...',
  'Adding explanations and visuals...',
  'Finalizing your video!',
];
const sortMap: Record<'Oldest' | 'Newest', string> = {
  Oldest: 'Oldest First',
  Newest: 'Newest First',
};
const orderOptions: Array<{
  label: string;
  value: 'Newest' | 'Oldest';
}> = [
  {
    label: 'Newest First',
    value: 'Newest',
  },
  {
    label: 'Oldest First',
    value: 'Oldest',
  },
];
const statusOptions: Array<{
  label: string;
  value: string;
}> = [
  {
    label: 'All Status',
    value: 'All Status',
  },
  {
    label: 'Ready to learn',
    value: 'VSucc',
  },
  {
    label: 'Generating',
    value: 'VProc',
  },
  {
    label: 'Generation Failed',
    value: 'VFail',
  },
];
const statusMap: Record<string, string> = {
  'All Status': 'All Status',
  VSucc: 'Ready to learn',
  VProc: 'Generating',
  VFail: 'Generation Failed',
};

// const subjectColors: Record<Video["subject"], { bg: string; text: string }> = {
//   Mathematics: { bg: "bg-blue-100", text: "text-blue-700" },
//   Physics: { bg: "bg-cyan-100", text: "text-cyan-700" },
//   Biology: { bg: "bg-emerald-100", text: "text-emerald-700" },
//   Chemistry: { bg: "bg-purple-100", text: "text-purple-700" },
//   History: { bg: "bg-orange-100", text: "text-orange-700" },
//   Geography: { bg: "bg-yellow-100", text: "text-yellow-700" },
//   Psychology: { bg: "bg-pink-100", text: "text-pink-700" },
//   "Computer Science": { bg: "bg-indigo-100", text: "text-indigo-700" },
//   AI: { bg: "bg-violet-100", text: "text-violet-700" },
//   Economics: { bg: "bg-green-100", text: "text-green-700" },
//   Literature: { bg: "bg-rose-100", text: "text-rose-700" },
//   Engineering: { bg: "bg-slate-100", text: "text-slate-700" },
// };

export function MyVideosPage({
  userid,
  videos,
  updateFlag,
  onVideoClick,
  onUpdateVideoStatus,
  onUpdateVideoProgress,
  onDeleteVideo,
  onToggleFavorite,
  onReGenerate,
  onNavigateToHome,
  onCancelGenerate,
  handleHasUpdated,
}: MyVideosPageProps) {
  const [progressText, setProgressText] = useState(0);
  const [progressValue, setProgressValue] = useState(0);
  const [selectedSubject, setSelectedSubject] =
    useState<Subject>('All Subjects');
  const [selectedStatus, setSelectedStatus] = useState<string>('All Status');
  const updateMyVideoListRef = useRef<number | undefined>(void 0);
  const [sortOrder, setSortOrder] = useState<'Newest' | 'Oldest'>('Newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [showQuestionInput, setShowQuestionInput] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  // const [copied, setCopied] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState<string | null>(null);
  const [myVideoList, setMyVideoList] = useState<VideoCard[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const subjectParam = searchParams.get('subject');

  // Sort subjects alphabetically
  // const sortedSubjects = Object.keys(subjectColors).sort();
  const sortedSubjects = (Object.keys(mySubjectColors) as Subject[]).sort();

  useEffect(() => {
    if (subjectParam) {
      if (
        Object.keys(mySubjectColors).includes(subjectParam) ||
        subjectParam === 'All Subjects'
      ) {
        setSelectedSubject(subjectParam as Subject);
      }
    } else {
      setSelectedSubject('All Subjects');
    }
  }, [subjectParam]);

  useEffect(() => {
    const generatingVideo = videos.find((v) => v.status === 'generating');
    if (!generatingVideo) {
      setProgressValue(0);
      setProgressText(0);
      return;
    }

    // Update progress text every 15 seconds
    const textInterval = setInterval(() => {
      setProgressText((prev) => (prev + 1) % progressStages.length);
    }, 15000);

    // Update progress value smoothly
    const valueInterval = setInterval(() => {
      setProgressValue((prev) => {
        const newValue = prev + 100 / 60;
        if (newValue >= 100) {
          onUpdateVideoProgress(generatingVideo.id, 100);
          return 100;
        }
        onUpdateVideoProgress(generatingVideo.id, newValue);
        return newValue;
      });
    }, 1000);

    // Complete after 60 seconds
    const completeTimer = setTimeout(() => {
      onUpdateVideoStatus(generatingVideo.id, 'v_succ');
    }, 60000);

    return () => {
      clearInterval(textInterval);
      clearInterval(valueInterval);
      clearTimeout(completeTimer);
    };
  }, [videos, onUpdateVideoStatus, onUpdateVideoProgress]);

  // Filter and sort videos
  // const filteredVideos = videos;
  useEffect(() => {
    if (updateFlag) {
      updateMyVideoList();
      handleHasUpdated();
    }
  }, [updateFlag]);
  const request = async () => {
    try {
      if (userid) {
        // For 'Others', we fetch all and filter client-side because backend might not map 'Others' to empty/null subject
        const apiSubject =
          selectedSubject === 'All Subjects' ? '' : selectedSubject;

        const res = await getMyVideoList(
          apiSubject,
          selectedStatus == 'All Status' ? '' : selectedStatus,
          sortOrder,
        );
        let fetchedCards = res?.cards || [];

        if (selectedSubject === 'Others') {
          // Filter for videos that don't have a subject or subject is 'Others'
          fetchedCards = fetchedCards.filter(
            (v) => !v.subject || v.subject === 'Others',
          );
        }

        setMyVideoList(fetchedCards);
        const isGeneratingVideo = fetchedCards.find(
          (item) => item.status == 'v_proc' || item.status == 'q_proc',
        );
        if (isGeneratingVideo) {
          clearTimeout(updateMyVideoListRef.current);
          updateMyVideoListRef.current = window.setTimeout(request, 5000);
        }
      }
    } catch (e) {
      clearTimeout(updateMyVideoListRef.current);
      toast.error(e.message || 'request error');
    }
  };
  const updateMyVideoList = async () => {
    setIsLoading(true);
    clearTimeout(updateMyVideoListRef.current);
    await request();
    setIsLoading(false);
  };
  useEffect(() => {
    updateMyVideoList();
  }, [userid, sortOrder, selectedStatus, selectedSubject]);

  const handleShare = (videoId: string) => {
    const url = `${location.protocol}//${location.host}/#/video/${videoId}`;
    setShareUrl(url);
    setShareDialogOpen(true);
    // setCopied(false);
    // setShareUrl(url);
    // setShareDialogOpen(true);
    // setCopied(false);
  };

  // const handleCopyUrl = () => {
  //   navigator.clipboard.writeText(shareUrl);
  //   setCopied(true);
  //   setTimeout(() => {
  //     toast.success('Link copied to clipboard');
  //     setShareDialogOpen(false);
  //     setCopied(false);
  //   }, 1000);
  // };

  const handleDeleteClick = (videoId: string) => {
    setVideoToDelete(videoId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (videoToDelete) {
        // onDeleteVideo(videoToDelete);
        await deleteMyVideo(videoToDelete);
        // updateMyVideoList();
        updateMyVideoList();
        toast.success('Video deleted');
        setVideoToDelete(null);
      }
      setDeleteDialogOpen(false);
    } catch (e) {
      toast.error('Video delete fail');
    }
    // if (videoToDelete) {
    //   onDeleteVideo(videoToDelete);
    //   toast.success('Video deleted');
    //   setVideoToDelete(null);
    // }
    // setDeleteDialogOpen(false);
  };
  const myFilterVideoList = myVideoList;
  // console.log(myFilterVideoList);
  return (
    <>
      <div className="min-h-screen pt-24 pb-12 px-10 bg-white">
        <div className="max-w-[1296px] mx-auto">
          <div className="mb-8 animate-in fade-in slide-in-from-top-4 duration-500">
            <h1 className="mb-2 font-alipuhui-b">My Solve Videos</h1>
            <p className="text-muted-foreground">
              View and manage your generated videos explanations
            </p>
          </div>

          {/* Filters and Sort */}
          <div className="flex flex-wrap gap-4 mb-8 justify-between animate-in fade-in slide-in-from-top-4 duration-500 delay-100">
            {/* Left side - Filters */}
            <div className="flex gap-4">
              {/* Subject Filter */}

              {subjectParam || isLoading ? (
                <DropdownMenu open={false}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="rounded-[18px]! bg-white border-[rgba(0,0,0,0.08)] h-9"
                    >
                      <Filter className="w-4 h-4 mr-2" />
                      {selectedSubject}
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="w-56 rounded-xl"
                  >
                    {sortedSubjects.map((subject) => (
                      <DropdownMenuItem
                        key={subject}
                        onClick={() => setSelectedSubject(subject)}
                      >
                        {subject}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="rounded-[18px]! bg-white border-[rgba(0,0,0,0.08)] h-9"
                    >
                      <Filter className="w-4 h-4 mr-2" />
                      {selectedSubject}
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="w-56 rounded-xl"
                  >
                    {sortedSubjects.map((subject) => (
                      <DropdownMenuItem
                        key={subject}
                        onClick={() => setSelectedSubject(subject)}
                      >
                        {subject}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Status Filter */}
              {isLoading ? (
                <DropdownMenu open={false}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="rounded-[18px]! bg-white border-[rgba(0,0,0,0.08)] h-9"
                    >
                      <Filter className="w-4 h-4 mr-2" />
                      {statusMap[selectedStatus]}
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="rounded-xl">
                    {statusOptions.map((item) => (
                      <DropdownMenuItem
                        key={item.value}
                        onClick={() => setSelectedStatus(item.value)}
                      >
                        {item.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="rounded-[18px]! bg-white border-[rgba(0,0,0,0.08)] h-9"
                    >
                      <Filter className="w-4 h-4 mr-2" />
                      {statusMap[selectedStatus]}
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="rounded-xl">
                    {statusOptions.map((item) => (
                      <DropdownMenuItem
                        key={item.value}
                        onClick={() => setSelectedStatus(item.value)}
                      >
                        {item.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {/* Right side - Sort Only */}
            <div className="flex gap-4">
              {/* Sort Order */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="rounded-[18px]! bg-white border-[rgba(0,0,0,0.08)] h-9"
                  >
                    {sortMap[sortOrder]}
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-xl">
                  {orderOptions.map((item) => (
                    <DropdownMenuItem
                      key={item.value}
                      onClick={() => setSortOrder(item.value)}
                    >
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Videos Grid */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20 animate-in fade-in duration-500">
              <Loader2 className="w-12 h-12 text-black animate-spin" />
            </div>
          ) : myFilterVideoList.length === 0 ? (
            <div className="text-center py-16 animate-in fade-in duration-500">
              <div className="mb-4 flex justify-center">
                <img
                  src="https://cdn.tiantiantiaosheng.com/dataupload/tool/2025-11-10/312521f1-74a7-4d0d-b3e3-dd14dd7f9634/6edd4dcb-3619-4d12-bfa3-34f4ae2144b9.png"
                  alt="No videos"
                  style={{ width: '140px', height: '140px' }}
                />
              </div>
              <h3 className="mb-2 text-black">No videos found</h3>
              <p className="text-muted-foreground">
                {selectedSubject !== 'All Subjects' ||
                selectedStatus !== 'All Status'
                  ? 'Try adjusting your filters'
                  : 'Start generating your first video explanation!'}
              </p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-4 mb-8 justify-start video-list">
              {myFilterVideoList.map((video, index) => (
                <div className="mb-4">
                  <MyVideoCard
                    key={`${video.video_id}_${index}_${video.video_url}`}
                    video={video}
                    index={index}
                    subjectColorMap={mySubjectColors}
                    onVideoClick={onVideoClick}
                    onShare={handleShare}
                    onDelete={handleDeleteClick}
                    onCancelGenerate={onCancelGenerate}
                    onReGenerate={onReGenerate}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Share Dialog */}
          {shareDialogOpen && (
            <ShareDialog
              shareUrl={shareUrl}
              setShareDialogOpen={setShareDialogOpen}
              shareDialogOpen={shareDialogOpen}
            />
          )}
          {/* <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Share Video</DialogTitle>
                <DialogDescription>
                  Copy the link below to share this video with others
                </DialogDescription>
              </DialogHeader>
              <div className="flex items-center space-x-2">
                <div className="grid flex-1 gap-2">
                  <Input readOnly value={shareUrl} className="rounded-xl" />
                </div>
                <Button
                  size="icon"
                  onClick={handleCopyUrl}
                  className="rounded-xl"
                >
                  {copied ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <DialogFooter className="sm:justify-start">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShareDialogOpen(false)}
                  className="rounded-xl"
                >
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog> */}

          {/* Delete Confirmation Dialog */}
          <AlertDialog
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete
                  your video.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteConfirm}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Back to Home Button */}
          <button
            onClick={onNavigateToHome}
            className="fixed bottom-8 right-8 bg-primary text-black px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center gap-2 font-normal text-[16px] leading-[24px] tracking-[-0.3125px]"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
        </div>
      </div>
    </>
  );
}
